import React, { useState } from "react";
import { Line, Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import SubPageLayout from "../../../components/shared/SubPageLayout";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

const FirmOverview = () => {
  const breadcrumbItems = [
    { label: "Administrative", link: "/administrative" },
    { label: "Firm Overview", link: "/administrative/firm-overview" },
  ];

  // State for date range filter
  const [dateRange, setDateRange] = useState("monthly");

  // Mock data for KPIs
  const [kpiData] = useState({
    totalSales: {
      value: "$2.5M",
      trend: "+12%",
      isPositive: true,
    },
    newClients: {
      value: "45",
      trend: "+8%",
      isPositive: true,
    },
    activeCampaigns: {
      value: "12",
      trend: "-2",
      isPositive: false,
    },
    revenueGrowth: {
      value: "15.8%",
      trend: "+2.3%",
      isPositive: true,
    },
    clientRetention: {
      value: "94%",
      trend: "+1%",
      isPositive: true,
    },
  });

  // Mock data for charts
  const [chartData] = useState({
    salesTrend: {
      labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      datasets: [
        {
          label: "Sales ($)",
          data: [350000, 420000, 380000, 450000, 480000, 520000],
          borderColor: "#4F46E5",
          tension: 0.4,
          fill: true,
          backgroundColor: "rgba(79, 70, 229, 0.1)",
        },
      ],
    },
    departmentalSales: {
      labels: [
        "Wealth Management",
        "Insurance",
        "Tax Planning",
        "Estate Planning",
      ],
      datasets: [
        {
          label: "Revenue by Department",
          data: [850000, 650000, 450000, 550000],
          backgroundColor: [
            "rgba(79, 70, 229, 0.8)",
            "rgba(59, 130, 246, 0.8)",
            "rgba(16, 185, 129, 0.8)",
            "rgba(245, 158, 11, 0.8)",
          ],
        },
      ],
    },
    marketingMetrics: {
      labels: ["Email", "Social Media", "Webinars", "Referrals", "Direct"],
      datasets: [
        {
          label: "Lead Sources",
          data: [35, 25, 20, 15, 5],
          backgroundColor: [
            "#4F46E5",
            "#3B82F6",
            "#10B981",
            "#F59E0B",
            "#EF4444",
          ],
        },
      ],
    },
  });

  // Mock data for employees
  const [employees] = useState([
    {
      id: 1,
      name: "John Smith",
      title: "Senior Financial Advisor",
      department: "Wealth Management",
      email: "john.smith@example.com",
      performance: {
        clientSatisfaction: 95,
        revenueGenerated: "$450K",
        activeClients: 45,
      },
    },
    {
      id: 2,
      name: "Sarah Johnson",
      title: "Tax Planning Specialist",
      department: "Tax Services",
      email: "sarah.j@example.com",
      performance: {
        clientSatisfaction: 92,
        revenueGenerated: "$380K",
        activeClients: 38,
      },
    },
    {
      id: 3,
      name: "Michael Brown",
      title: "Insurance Advisor",
      department: "Insurance",
      email: "michael.b@example.com",
      performance: {
        clientSatisfaction: 88,
        revenueGenerated: "$290K",
        activeClients: 42,
      },
    },
  ]);

  // Mock data for upcoming events
  const [events] = useState([
    {
      id: 1,
      title: "Quarterly Planning Meeting",
      date: "2024-03-25",
      time: "10:00 AM",
      type: "Internal",
      attendees: 12,
    },
    {
      id: 2,
      title: "Client Appreciation Event",
      date: "2024-04-15",
      time: "6:00 PM",
      type: "External",
      attendees: 75,
    },
    {
      id: 3,
      title: "Team Training Workshop",
      date: "2024-03-28",
      time: "2:00 PM",
      type: "Internal",
      attendees: 25,
    },
  ]);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
      },
    },
  };

  const lineChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: (value) => "$" + value / 1000 + "K",
        },
      },
    },
  };

  return (
    <SubPageLayout
      title="Firm Overview"
      subtitle="Monitor key metrics, analyze performance, and manage firm-wide activities"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="space-y-6">
        {/* Header Actions */}
        <div className="flex justify-between items-center">
          <div className="flex space-x-4">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <div className="flex space-x-4">
            <button className="px-4 py-2 text-gray-700 dark:text-gray-300 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
              Refresh Data
            </button>
            <button className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90">
              Export Reports
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Object.entries(kpiData).map(([key, data]) => (
            <div key={key} className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-500 mb-2">
                {key.replace(/([A-Z])/g, " $1").trim()}
              </h3>
              <div className="flex items-end justify-between">
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{data.value}</p>
                <span
                  className={`flex items-center text-sm ${
                    data.isPositive ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {data.isPositive ? "↑" : "↓"} {data.trend}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales Trend */}
          <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Sales Trend
            </h2>
            <div className="h-64">
              <Line data={chartData.salesTrend} options={lineChartOptions} />
            </div>
          </div>

          {/* Departmental Sales */}
          <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Revenue by Department
            </h2>
            <div className="h-64">
              <Bar data={chartData.departmentalSales} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* Employee Management */}
        <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Employee Directory
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface-default dark:bg-surface-default divide-y divide-gray-200">
                {employees.map((employee) => (
                  <tr key={employee.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {employee.name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {employee.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {employee.department}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        Revenue: {employee.performance.revenueGenerated}
                        <br />
                        Satisfaction: {employee.performance.clientSatisfaction}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-500">
                      <button className="text-[#202EC6] hover:text-opacity-80">
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Marketing and Events */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Marketing Analytics */}
          <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
            <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
              Lead Sources
            </h2>
            <div className="h-64">
              <Pie data={chartData.marketingMetrics} options={chartOptions} />
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
            <div className="p-6 border-b">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Upcoming Events
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {event.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-500">
                        {new Date(event.date).toLocaleDateString()} at{" "}
                        {event.time}
                      </p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          event.type === "Internal"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {event.type}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-500">
                        {event.attendees} attendees
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SubPageLayout>
  );
};

export default FirmOverview;
